import { networkId } from '@/composables/useNetwork';
import { isStable } from '@/composables/usePoolHelpers';
import { oneSecondInMs } from '@/composables/useTime';
import { getBalancerSDK } from '@/dependencies/balancer-sdk';
import { bnum } from '@/lib/utils';
import { captureBalancerException } from '@/lib/utils/errors';
import { PoolApr } from '@/providers/local/pool-rewards.provider';
import {
  OnchainPoolData,
  Pool,
  PoolToken,
  RawOnchainPoolData,
} from '@/services/pool/types';
import { TokenInfoMap } from '@/types/TokenList';
import { AprBreakdown, Pool as SDKPool } from '@xclabs/balancer-sdk';
import { differenceInWeeks } from 'date-fns';
import { burrifiedPoolType } from './burrifier';
import { OnchainDataFormater } from './decorators/onchain-data.formater';

export default class PoolService {
  private _poolApr?: PoolApr;

  constructor(public pool: Pool, public poolAprRef?: Ref<PoolApr | undefined>) {
    if (poolAprRef) {
      this._poolApr = poolAprRef.value;
      watch(poolAprRef, newValue => {
        this._poolApr = newValue;
      });
    }

    this.format();
  }

  /**
   * @summary Statically format various pool attributes.
   */
  public format(): Pool {
    this.pool.isNew = this.isNew;
    this.pool.chainId = networkId.value;
    this.formatPoolTokens();
    this.burrifyPoolType();
    return this.pool;
  }

  burrifyPoolType = () => {
    this.pool.burrifiedPoolType = burrifiedPoolType(this.pool.poolType);
  };

  public get bptPrice(): string {
    return bnum(this.pool.totalLiquidity).div(this.pool.totalShares).toString();
  }

  /**
   * @summary Calculates and sets total liquidity of pool.
   */
  public async setTotalLiquidity(): Promise<string> {
    let totalLiquidity = this.pool.totalLiquidity;

    try {
      const sdkTotalLiquidity = await getBalancerSDK().pools.liquidity(
        this.pool as unknown as SDKPool
      );
      // if totalLiquidity can be computed from coingecko prices, use that
      // else, use the value retrieved from the subgraph
      if (bnum(totalLiquidity).gt(0)) {
        totalLiquidity = sdkTotalLiquidity;
      }
    } catch (error) {
      captureBalancerException({ error });
      console.error(`Failed to calc liquidity for: ${this.pool.id}`, error);
    }

    return (this.pool.totalLiquidity = totalLiquidity);
  }

  /**
   * @summary Calculates APRs for pool.
   */
  public async setAPR(): Promise<AprBreakdown> {
    if (!this._poolApr) {
      // Use BalancerSDK to compute the APRs if not available from Burrbear API
      try {
        const sdkApr = await getBalancerSDK().pools.apr(this.pool);
        if (sdkApr) {
          return (this.pool.apr = sdkApr);
        }
      } catch (error) {
        captureBalancerException({ error });
        console.error(`Failed to calc APR for: ${this.pool.id}`, error);
      }
      return this.pool.apr as AprBreakdown;
    }

    const swapFeesApr =
      Number(
        this._poolApr.components.find(c => c.type === 'SWAP_FEES')?.value ?? '0'
      ) * 100;

    const stakingComponents =
      this._poolApr.components.filter(c => c.type !== 'SWAP_FEES') || [];
    const rewardAPRs = this._rewardTokensAPR();

    const minTotalApr = Number(this._poolApr.min) * 100;
    const maxTotalApr = Number(this._poolApr.max) * 100;

    const apr: AprBreakdown = {
      min: minTotalApr,
      max: maxTotalApr,
      swapFees: swapFeesApr,
      rewardAprs: {
        total: rewardAPRs.total,
        min: rewardAPRs.min,
        max: rewardAPRs.max,
        breakdown: stakingComponents.reduce((acc, comp) => {
          const label = `${comp.rewardName ?? comp.type}`;
          acc[label] = Number(comp.value) * 100;
          return acc;
        }, {} as Record<string, number>),
        pointsMetadata: stakingComponents
          .filter(c => c.type === 'POINTS')
          .map(c => ({
            name: c.rewardName ?? '',
            label: c.rewardDescription ?? '',
            iconUrl: c.rewardLogoUrl ?? '',
          })),
      },
      tokenAprs: {
        total: 0,
        breakdown: {},
      },
      stakingApr: {
        min: 0,
        max: 0,
      },
      protocolApr: 0,
    };

    return (this.pool.apr = apr);
  }

  formatPoolTokens(): PoolToken[] {
    if (isStable(this.pool.poolType)) return this.pool.tokens;

    return (this.pool.tokens = this.pool.tokens.sort(
      (a, b) => parseFloat(b.weight || '0') - parseFloat(a.weight || '0')
    ));
  }

  public setFeesSnapshot(poolSnapshot: Pool | undefined): string {
    let snapshotFees = '0';
    if (poolSnapshot) snapshotFees = poolSnapshot.totalSwapFee || '0';

    const feesSnapshot = bnum(this.pool.totalSwapFee || 0)
      .minus(snapshotFees)
      .toString();

    return (this.pool.feesSnapshot = feesSnapshot);
  }

  public setVolumeSnapshot(poolSnapshot: Pool | undefined): string {
    let snapshotVolume = '0';
    if (poolSnapshot) snapshotVolume = poolSnapshot.totalSwapVolume || '0';

    const volumeSnapshot = bnum(this.pool.totalSwapVolume || 0)
      .minus(snapshotVolume)
      .toString();

    return (this.pool.volumeSnapshot = volumeSnapshot);
  }

  public setOnchainData(
    rawOnchainData: RawOnchainPoolData,
    tokenMeta: TokenInfoMap
  ): OnchainPoolData | undefined {
    try {
      const onchainData = new OnchainDataFormater(
        this.pool,
        rawOnchainData,
        tokenMeta
      );
      this.pool.isInRecoveryMode = rawOnchainData.isInRecoveryMode;
      this.pool.isPaused = rawOnchainData.isPaused;
      return (this.pool.onchain = onchainData.format());
    } catch (e) {
      console.warn(e);
    }
  }

  public get isNew(): boolean {
    if (!this.pool.createTime) return false;

    return (
      differenceInWeeks(Date.now(), this.pool.createTime * oneSecondInMs) < 1
    );
  }

  _rewardTokensAPR() {
    let pointsSum = 0;
    const baseRewards: number[] = [];

    // Iterate through rewards
    this._poolApr?.components.forEach(reward => {
      const value = parseFloat(reward.value); // Convert string to number
      if (reward.type === 'POINTS') {
        pointsSum += value * 100;
      } else if (reward.type === 'BASE_REWARDS' && value > 0) {
        baseRewards.push(value * 100);
      }
    });

    // If no BASE_REWARDS, return points sum as both min and max
    if (baseRewards.length === 0) {
      return {
        total: pointsSum,
        min: undefined,
        max: undefined,
      };
    }

    // Calculate min and max total values
    const minBaseReward = Math.min(...baseRewards);
    const maxBaseReward = Math.max(...baseRewards);
    const minTotal = pointsSum + minBaseReward;
    const maxTotal = pointsSum + maxBaseReward;

    // Return range as object
    return {
      total: maxTotal,
      min: minTotal,
      max: maxTotal,
    };
  }
}
